import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  chargingApi,
  type PayFrom,
  type SessionOut,
  type SessionState
} from '@/api/charging';
import { pointsApi, type PointDetailOut } from '@/api/points';
import { stationsApi, type StationDetailOut } from '@/api/stations';
import { amenitiesApi } from '@/api/amenities';
import { useAuthStore } from '@/store/auth';
import { useChargingSocket } from '@/hooks/useChargingSocket';
import { useChargeEta, type ChargeEta } from '@/hooks/useChargeEta';
import { DevotionalCard } from '@/components/charging/DevotionalCard';
import { AmenityTiles } from '@/components/stations/AmenityTiles';
import { Eyebrow } from '@/components/ui/Typography';
import { formatBRL, formatKw, formatKwh } from '@/lib/format';
import { friendlyMessage } from '@/lib/errors';

const STATE_LABELS: Record<SessionState, string> = {
  reserving: 'Preparando recarga…',
  starting: 'Iniciando recarga… aguarde',
  active: 'Carregando',
  stopping: 'Encerrando…',
  completed: 'Concluída',
  failed: 'Falhou',
  cancelled: 'Cancelada'
};

const TERMINAL: SessionState[] = ['completed', 'failed', 'cancelled'];

// Mesma convenção do Perfil e da landing /qr.
const SUPPORT_WHATSAPP_URL =
  import.meta.env.VITE_SUPPORT_WHATSAPP_URL ?? 'https://wa.me/5527988231783';

// Cancel em STARTING é bloqueado nos primeiros 30s pra evitar sessão fantasma
// no carregador (incidente Samuel 2026-05-09 — docs/bugs/2026-05-09-delete-sessions-premature.md
// e CLAUDE.md §3.8). Não baixar esse valor sem coordenar com o backend.
const STARTING_CANCEL_COOLDOWN_SECONDS = 30;

/**
 * ActiveCharge — única tela "Ousada" (cinematográfica) do app, na variante
 * "Imersiva · número gigante" do Claude Design (mapfirst-charging.jsx ·
 * `ChargingImmersiveNS`, pedido Saulo 2026-07-08): número gigante com
 * gradient cream→azul-gelo, barra fina now→80→100, pill de stats ao vivo
 * (kW · kWh · R$), CTA do aspirador, ETAs "até 80%/100%" estimadas no app
 * e amenidades da estação — tudo numa viewport fixa (NSS, sem scroll).
 */
export default function ActiveCharge() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);

  const [session, setSession] = useState<SessionOut | null>(null);
  const [point, setPoint] = useState<PointDetailOut | null>(null);
  const [station, setStation] = useState<StationDetailOut | null>(null);
  const [powerKw, setPowerKw] = useState<number | null>(null);
  const [socPercent, setSocPercent] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Falha ao ENCERRAR é separada do erro de carregamento de propósito: `error`
  // troca a tela inteira por um card, e fazer isso quando o stop falha esconde
  // justamente o kWh/R$ que continuam subindo — e o botão de tentar de novo.
  // Ver docs/bugs/2026-08-22-stop-rejected-ignorado-subcobranca.md.
  const [stopError, setStopError] = useState<string | null>(null);
  const [stopping, setStopping] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [confirmStop, setConfirmStop] = useState(false);
  // Aviso "sua recarga vai parar em breve" (saldo acabando): vem do GET da
  // sessão e do frame `balance_warning` do WS — este pelo `onEvent`, porque
  // chega colado no `meter_update` e se perderia no `lastEvent`.
  const [balanceWarning, setBalanceWarning] = useState(false);
  const endedRef = useRef(false);

  const lastEvent = useChargingSocket(sessionId ?? null, token, (ev) => {
    if (ev.event === 'balance_warning' && ev.session_id === sessionId) {
      setBalanceWarning(ev.balance_warning === true);
    }
  });
  const eta = useChargeEta(socPercent, session?.state ?? 'reserving');

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    chargingApi
      .get(sessionId)
      .then(async (s) => {
        if (cancelled) return;
        setSession(s);
        // Snapshot da última leitura que o backend já conhece — abre a tela
        // com o SoC/potência correntes em vez de esperar o próximo push do WS
        // (era a causa do "0% travado" enquanto a leitura não chegava).
        if (s.soc_percent !== null && s.soc_percent !== undefined) {
          setSocPercent(Number(s.soc_percent));
        }
        if (s.power_kw !== null && s.power_kw !== undefined) {
          setPowerKw(Number(s.power_kw));
        }
        setBalanceWarning(s.balance_stop_warning === true);
        if (TERMINAL.includes(s.state)) {
          navigate(`/receipt/${sessionId}`, { replace: true });
          return;
        }
        try {
          const p = await pointsApi.get(s.point_id);
          if (!cancelled) setPoint(p);
        } catch {
          /* nome da station é só detalhe visual */
        }
      })
      .catch((err) => {
        if (!cancelled) setError(friendlyMessage(err, 'Falha ao carregar sessão'));
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, navigate]);

  // Station da sessão — alimenta amenidades + CTA do aspirador na própria
  // tela (design Imersiva: é DURANTE a recarga que o cliente usa os serviços).
  // `StationCharger.id` === `charging_points.id` === `session.point_id`.
  // Falha/não achou → seções somem, tela não quebra.
  const pointId = session?.point_id ?? null;
  useEffect(() => {
    if (!pointId) return;
    let cancelled = false;
    stationsApi
      .listDetailed()
      .then((list) => {
        if (cancelled) return;
        setStation(
          list.find((s) => s.chargers.some((c) => c.id === pointId)) ?? null
        );
      })
      .catch(() => {
        /* amenidades são opcionais */
      });
    return () => {
      cancelled = true;
    };
  }, [pointId]);

  // Preço de entrada do aspirador, pro CTA anunciar o valor ANTES do toque.
  // Motivo: a análise de abandono de 2026-08-22 mostrou que 58 de 59 aberturas
  // da oferta vinham de caminhos SEM preço visível, e que a mediana de
  // permanência na tela era de 9s — a tela de oferta virou o momento do
  // "reveal". Mostrar aqui separa quem não quer pelo preço (não abre) de quem
  // desiste por outro motivo. Uma requisição, uma vez, e só se há aspirador.
  const stationId = station?.id ?? null;
  const hasVacuumCapability = station?.capabilities?.['vacuum'] === true;
  const [vacuumFrom, setVacuumFrom] = useState<number | null>(null);
  const [vacuumCourtesy, setVacuumCourtesy] = useState(false);
  useEffect(() => {
    if (!stationId || !hasVacuumCapability) return;
    let cancelled = false;
    amenitiesApi
      .devices(stationId)
      .then((list) => {
        if (cancelled) return;
        const precos = list.flatMap((d) => d.blocks).map((b) => Number(b.price_brl));
        setVacuumFrom(precos.length > 0 ? Math.min(...precos) : null);
        setVacuumCourtesy(list.some((d) => d.courtesy_available === true));
      })
      .catch(() => {
        /* sem preço o CTA volta ao texto simples — nunca some */
      });
    return () => {
      cancelled = true;
    };
  }, [stationId, hasVacuumCapability]);

  useEffect(() => {
    if (!lastEvent || !sessionId) return;
    if (lastEvent.session_id !== sessionId) return;
    // Tratado no `onEvent` do socket; não carrega leitura nova.
    if (lastEvent.event === 'balance_warning') return;

    setSession((prev) =>
      prev
        ? {
            ...prev,
            state: (lastEvent.state as SessionState) || prev.state,
            energy_kwh: lastEvent.energy_kwh || prev.energy_kwh,
            cost_brl: lastEvent.cost_brl || prev.cost_brl
          }
        : prev
    );

    if (lastEvent.power_kw !== null && lastEvent.power_kw !== undefined) {
      setPowerKw(Number(lastEvent.power_kw));
    }
    if (lastEvent.soc_percent !== null && lastEvent.soc_percent !== undefined) {
      setSocPercent(Number(lastEvent.soc_percent));
    }

    if (lastEvent.event === 'session_ended' && !endedRef.current) {
      endedRef.current = true;
      chargingApi.get(sessionId).then(() => {
        navigate(`/receipt/${sessionId}`, { replace: true });
      });
    }
  }, [lastEvent, sessionId, navigate]);

  // Fallback polling em 'stopping' (cobre WS perdido).
  useEffect(() => {
    if (!sessionId || !session || session.state !== 'stopping') return;
    if (endedRef.current) return;
    const t = window.setInterval(async () => {
      try {
        const updated = await chargingApi.get(sessionId);
        if (TERMINAL.includes(updated.state)) {
          clearInterval(t);
          if (!endedRef.current) {
            endedRef.current = true;
            navigate(`/receipt/${sessionId}`, { replace: true });
          }
        } else {
          setSession(updated);
        }
      } catch {
        /* tenta de novo no próximo tick */
      }
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.state, sessionId, navigate]);

  // Guardrail: se state local virar terminal por qualquer caminho, navega.
  useEffect(() => {
    if (!sessionId || !session) return;
    if (endedRef.current) return;
    if (TERMINAL.includes(session.state)) {
      endedRef.current = true;
      navigate(`/receipt/${sessionId}`, { replace: true });
    }
  }, [session, sessionId, navigate]);

  async function doStop() {
    if (!session) return;
    setConfirmStop(false);
    setStopping(true);
    setStopError(null);
    try {
      const updated = await chargingApi.stop(session.id);
      setSession(updated);
      setStopError(null);
    } catch (err) {
      // Nada de `setError` aqui: a sessão continua ativa e o cliente precisa
      // seguir vendo o consumo — e poder tentar de novo (a falha do Hub costuma
      // ser transitória).
      setStopError(friendlyMessage(err, 'Falha ao encerrar'));
      setStopping(false);
    }
  }

  function onStopClick() {
    if (!session) return;
    if (session.state === 'starting') {
      setConfirmStop(true);
      return;
    }
    void doStop();
  }

  function onClose() {
    navigate('/', { replace: true });
  }

  if (error) {
    return (
      <div className="rounded-3xl bg-white p-5 text-sm text-bow-red shadow-soft">
        {error}
      </div>
    );
  }
  if (!session) {
    return <div className="h-64 animate-pulse rounded-3xl bg-white shadow-soft" />;
  }

  const energyKwh = Number(session.energy_kwh);
  const tariff = Number(session.tariff_per_kwh_brl);
  const costFromApi = Number(session.cost_brl);
  const cost = costFromApi > 0 ? costFromApi : energyKwh * tariff;
  const limit = Number(session.limit_value);

  const elapsed = session.started_at
    ? Math.max(0, Math.floor((now - new Date(session.started_at).getTime()) / 1000))
    : 0;

  const cooldownRemaining =
    session.state === 'starting'
      ? Math.max(0, STARTING_CANCEL_COOLDOWN_SECONDS - elapsed)
      : 0;

  const canStop =
    session.state === 'active' ||
    session.state === 'reserving' ||
    (session.state === 'starting' && cooldownRemaining === 0);

  const showCooldownButton =
    session.state === 'starting' && cooldownRemaining > 0;

  const isStarting =
    session.state === 'starting' || session.state === 'reserving';

  // Charger autorizou (state=starting/active) mas energia ainda não está
  // fluindo (sem meter.updated). Cenário típico: usuário não plugou o cabo.
  // Sem este aviso, o usuário tende a interpretar "Iniciando recarga…" como
  // "estou esperando o sistema", quando o sistema é que está esperando ele
  // plugar. Incidente DayByDay 2026-05-10 originou-se daí.
  const showPlugCablePrompt =
    (session.state === 'starting' || session.state === 'active') &&
    powerKw === null &&
    energyKwh === 0;

  // O número grande tem 3 modos, em prioridade:
  //  - 'soc'    → % real da bateria. DC reporta SoC via OCPP; mostramos direto.
  //  - 'target' → % até o limite escolhido. Faz sentido p/ limite por valor
  //               (R$) ou energia (kWh): há uma meta concreta de progresso.
  //  - 'energy' → energia entregue (kWh) como herói. Carregadores AC nunca
  //               enviam SoC; em 'full_balance' não há meta (o teto é o saldo
  //               vivo + margem do nível, que muda durante a sessão), então um
  //               "%" daria sempre ~0% e enganoso — era a causa do "0%
  //               travado". Cobre também a janela inicial de uma sessão DC
  //               antes da 1ª leitura.
  const hasSoc = socPercent !== null;
  const targetProgress =
    session.limit_type === 'energy' && limit > 0
      ? Math.max(0, Math.min(1, energyKwh / limit))
      : session.limit_type === 'value' && limit > 0
        ? Math.max(0, Math.min(1, cost / limit))
        : null;
  const heroMode: 'soc' | 'target' | 'energy' = hasSoc
    ? 'soc'
    : targetProgress !== null
      ? 'target'
      : 'energy';

  const barProgress =
    heroMode === 'soc'
      ? Math.max(0, Math.min(1, (socPercent as number) / 100))
      : heroMode === 'target'
        ? (targetProgress as number)
        : null;

  const heroValue =
    heroMode === 'soc'
      ? String(Math.round(socPercent as number))
      : heroMode === 'target'
        ? String(Math.round((targetProgress as number) * 100))
        : formatKwh(energyKwh).replace(' kWh', '');
  const heroUnit = heroMode === 'energy' ? 'kWh' : '%';
  const displayLabel = isStarting
    ? 'Aguarde'
    : heroMode === 'soc'
      ? 'Carga'
      : heroMode === 'target'
        ? 'Sessão'
        : 'Energia';
  const eyebrowSuffix = isStarting
    ? ''
    : heroMode === 'soc'
      ? 'atual'
      : heroMode === 'energy'
        ? 'entregue'
        : 'em curso';

  const stationName =
    station?.name ?? session.station_name ?? point?.name ?? 'Estação Bow';
  const hasVacuum = station?.capabilities?.['vacuum'] === true;

  return (
    // Route imersiva — AppLayout detecta `/charging/*` e remove padding +
    // esconde BottomNav. Fundo navy radial do design ChargingImmersiveNS.
    // NSS: h-[100dvh] fixo, herói flex-1 absorve a folga e comprime.
    <div
      className="relative flex h-[100dvh] flex-col overflow-hidden text-bow-cream"
      style={{
        background:
          'radial-gradient(120% 75% at 50% 20%, #0A2E6E 0%, #001B74 60%, #001250 100%)'
      }}
    >
      {/* Aurora — faixa cônica verde→teal→azul no topo (design Imersiva). */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[55%]"
        aria-hidden
        style={{
          background:
            'conic-gradient(from 220deg at 50% 100%, transparent, rgba(0,192,114,0.35) 32%, rgba(0,187,222,0.35) 50%, rgba(0,144,226,0.35) 66%, transparent 82%)',
          filter: 'blur(40px)',
          opacity: 0.85
        }}
      />

      <div
        className="relative z-10 flex h-full min-h-0 flex-col gap-3 px-5 [@media(max-height:740px)]:gap-2"
        style={{
          paddingTop: 'max(1rem, env(safe-area-inset-top))',
          paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))'
        }}
      >
        {/* Header — live pill + close */}
        <div className="flex flex-shrink-0 items-center justify-between pt-2">
          <span className="inline-flex items-center gap-2 rounded-full border border-bow-cream/15 bg-bow-cream/10 px-3.5 py-2 text-xs font-semibold backdrop-blur-md">
            <span
              className="inline-block h-[7px] w-[7px] animate-pulse rounded-full bg-bow-green"
              style={{ boxShadow: '0 0 8px #00C072' }}
            />
            {STATE_LABELS[session.state]} · {stationName}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-bow-cream/15 bg-bow-cream/10 backdrop-blur-md transition hover:bg-bow-cream/15"
          >
            <CloseIcon />
          </button>
        </div>

        {stopError && (
          <div
            className="flex-shrink-0 rounded-2xl border border-bow-red/40 bg-bow-red/15 px-4 py-3 text-sm text-bow-cream backdrop-blur-md"
            role="alert"
            aria-live="assertive"
          >
            <div className="font-semibold">Não conseguimos encerrar</div>
            <p className="mt-1 text-bow-cream/80">{stopError}</p>
            <a
              href={SUPPORT_WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block font-semibold text-bow-cream underline underline-offset-2"
            >
              Falar com o suporte
            </a>
          </div>
        )}

        {showPlugCablePrompt && (
          <div
            className="flex-shrink-0 rounded-2xl border border-bow-cream/20 bg-bow-cream/10 px-4 py-3 text-sm text-bow-cream backdrop-blur-md"
            role="status"
            aria-live="polite"
          >
            <div className="font-semibold">Plugue o cabo no carro agora</div>
            <p className="mt-1 text-bow-cream/75">
              O carregador foi autorizado e está esperando o cabo. Se você não
              plugar em ~60 segundos, ele cancela sozinho. Você não está pagando
              nada enquanto isso.
            </p>
          </div>
        )}

        {balanceWarning && session.state === 'active' && (
          <BalanceStopBanner
            payFrom={session.pay_from ?? null}
            onTopUp={() =>
              navigate('/wallet/recharge', {
                state: { from: `/charging/${session.id}` }
              })
            }
          />
        )}

        {/* Herói — número gigante + barra fina + pill de stats */}
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2">
          <Eyebrow color="rgba(241,237,228,0.6)">
            {displayLabel}
            {eyebrowSuffix ? ` ${eyebrowSuffix}` : ''}
          </Eyebrow>
          <div
            style={{
              fontFamily: 'Chillax, system-ui, sans-serif',
              fontWeight: 500,
              // Clamp por largura E altura (lição do bug 2026-07-04) — modo
              // 'energy' ("12,4 kWh") tem string bem mais longa que "62%".
              fontSize:
                heroMode === 'energy' ? 'min(22vw, 13vh)' : 'min(33vw, 17vh)',
              whiteSpace: 'nowrap',
              lineHeight: 0.85,
              letterSpacing: '-0.06em',
              background: 'linear-gradient(180deg, #F1EDE4 0%, #8FD8F0 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent'
            }}
          >
            {heroValue}
            <span
              style={{
                WebkitTextFillColor: 'rgba(241,237,228,0.4)',
                background: 'none',
                fontSize: heroUnit === '%' ? '0.4em' : '0.26em'
              }}
            >
              {heroUnit === '%' ? '%' : ` ${heroUnit}`}
            </span>
          </div>

          {/* Barra fina now→80→100 (só quando há progresso determinado). */}
          {barProgress !== null && (
            <div className="mt-1 w-[78%]">
              <div className="relative h-2 overflow-hidden rounded-full bg-bow-cream/[0.14]">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${barProgress * 100}%`,
                    background: 'linear-gradient(90deg, #00C072, #00BBDE)'
                  }}
                />
                {heroMode === 'soc' && (
                  <span className="absolute left-[80%] top-[-3px] h-[14px] w-[2px] bg-bow-cream/60" />
                )}
              </div>
              <div className="mt-1.5 flex justify-between text-[10.5px] text-bow-cream/55">
                <span>agora</span>
                {heroMode === 'soc' ? (
                  <>
                    <span className="ml-auto mr-[18%] text-[#7FD8EE]">80%</span>
                    <span>100%</span>
                  </>
                ) : (
                  <span>limite</span>
                )}
              </div>
            </div>
          )}

          {/* Pill de stats ao vivo — ⚡ kW · kWh · R$ (design). */}
          <div className="mt-2 inline-flex items-center gap-2.5 rounded-full border border-bow-cream/15 bg-bow-cream/10 px-4 py-2 text-[13px] backdrop-blur-md">
            <span className="inline-flex items-center gap-1.5 font-semibold">
              <BoltIcon />{' '}
              {powerKw !== null
                ? formatKw(powerKw)
                : isStarting
                  ? '—'
                  : '0,0 kW'}
            </span>
            <span className="text-bow-cream/40">·</span>
            <span className="font-semibold">{formatKwh(energyKwh)}</span>
            <span className="text-bow-cream/40">·</span>
            <span className="font-semibold" style={{ color: '#FFB92A' }}>
              {formatBRL(cost)}
            </span>
          </div>
        </div>

        {/* Rodapé — CTA aspirador, ETAs, amenidades, devocional, encerrar */}
        <div className="flex flex-shrink-0 flex-col gap-3 [@media(max-height:740px)]:gap-2">
          {hasVacuum && station && (
            <VacuumCta
              fromBrl={vacuumFrom}
              courtesy={vacuumCourtesy}
              onClick={() => navigate(`/amenities/${station.id}/vacuum?origin=charging`)}
            />
          )}

          {eta && session.state === 'active' && <EtaCards eta={eta} />}

          {station && (
            <AmenityTiles station={station} tone="dark" excludeVacuum />
          )}

          {/* Cultura Bow — pausa de reflexão durante a espera. Só em telas
              altas (decisão Saulo 2026-07-08; NSS comprime o resto antes). */}
          {(session.state === 'active' || session.state === 'starting') && (
            <div className="[@media(max-height:740px)]:hidden">
              <DevotionalCard tone="dark" />
            </div>
          )}

          {(canStop || showCooldownButton) && (
            <button
              type="button"
              onClick={onStopClick}
              disabled={stopping || showCooldownButton}
              className="inline-flex h-[54px] flex-shrink-0 items-center justify-center rounded-full border-[1.5px] border-bow-cream/25 bg-bow-cream/[0.08] px-6 text-base font-semibold text-bow-cream backdrop-blur-md transition hover:bg-bow-cream/15 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {stopping
                ? 'Encerrando…'
                : showCooldownButton
                  ? `Aguarde ${cooldownRemaining}s para encerrar`
                  : 'Encerrar recarga'}
            </button>
          )}
        </div>
      </div>

      {confirmStop && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-sm rounded-3xl bg-bow-cream p-6 text-bow-royal shadow-2xl">
            <div className="text-lg font-semibold">
              {showPlugCablePrompt ? 'Você já plugou o cabo?' : 'Encerrar antes do início?'}
            </div>
            <p className="mt-2 text-sm text-bow-royal/75">
              {showPlugCablePrompt ? (
                <>
                  O carregador está esperando o cabo. Se você ainda não plugou,
                  plugue no carro <strong>antes de encerrar</strong> — a recarga
                  começa em poucos segundos depois. Encerrar agora sem plugar
                  faz o carregador desistir e exige um novo ciclo do equipamento.
                </>
              ) : (
                <>
                  Se o carregador já iniciou fisicamente a recarga, esta sessão será
                  cobrada pelo consumo real assim que terminar.
                </>
              )}
            </p>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmStop(false)}
                className="flex-1 rounded-full border border-bow-royal/15 bg-white px-4 py-3 text-sm font-semibold text-bow-royal"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={() => void doStop()}
                className="flex-1 rounded-full bg-bow-royal px-4 py-3 text-sm font-semibold text-bow-cream"
              >
                Encerrar mesmo assim
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** CTA do aspirador — "é quando o cliente usa" (design ChargingImmersiveNS ·
 * `VacuumCTA`): gradient esmeralda→teal, PNG oficial num círculo branco.
 *
 * `fromBrl` anuncia o preço ANTES do toque. Até 2026-08-22 o botão não tinha
 * valor nenhum, e 58 de 59 aberturas da oferta vinham por caminhos assim — a
 * tela de oferta virava o "reveal" do preço, com mediana de 9s até a
 * desistência. Com o valor aqui, quem não quer pagar não abre, e o que sobra
 * de abandono passa a apontar pra outra causa. `null` (preço ainda carregando
 * ou indisponível) cai no texto simples: o CTA nunca some por causa disso. */
export function VacuumCta({
  onClick,
  fromBrl,
  courtesy = false
}: {
  onClick: () => void;
  fromBrl?: number | null;
  /** Cliente ainda tem a 1ª aspiração grátis — anuncia isso no lugar do preço. */
  courtesy?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-[58px] w-full flex-shrink-0 items-center justify-center gap-3 rounded-full text-base font-bold tracking-tight text-bow-royal transition active:scale-[0.99] [@media(max-height:740px)]:h-[52px]"
      style={{
        background: 'linear-gradient(90deg, #00C072, #00BBDE)',
        boxShadow: '0 10px 26px rgba(0,192,114,0.35)'
      }}
    >
      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-bow-cream">
        <img
          src="/logo/aspirador-bow.png"
          alt=""
          className="h-7 w-7 object-contain"
        />
      </span>
      Aspirar meu carro
      {/* Cortesia tem precedência sobre o preço: "grátis" é a informação que
          decide, e mostrar os dois juntos confunde (o cliente lê o valor e
          acha que vai pagar). Quem já usou a dele volta a ver o preço. */}
      {courtesy ? (
        <span
          className="rounded-full px-2.5 py-0.5 text-[13px] font-bold"
          style={{ background: 'rgba(0,27,116,0.14)' }}
        >
          é grátis
        </span>
      ) : (
        fromBrl != null && (
          <span className="font-semibold opacity-80">
            · a partir de {formatBRL(fromBrl)}
          </span>
        )
      )}
      <ChevronRightIcon />
    </button>
  );
}

/** Aviso de que a recarga vai parar por saldo, uns minutos antes do corte
 * (backend `evaluate_balance_warning`). Motivo: relatório de desistência do
 * Yahoo (22/10/2026), 10,6% das sessões cortadas por saldo antes de 80% sem
 * aviso nenhum. O aporte estende a recarga sem encerrar, porque o teto
 * acompanha o saldo ao vivo; o fluxo de PIX/cartão volta pra cá pelo
 * `state.from`. Paga com cashback não ganha o botão: saldo novo não estende
 * recarga paga com cashback. Nunca mostra valor — a folga de dívida do nível
 * não é anunciada. */
export function BalanceStopBanner({
  payFrom,
  onTopUp
}: {
  payFrom: PayFrom | null;
  onTopUp: () => void;
}) {
  const bonus = payFrom === 'bonus';
  return (
    <div
      className="flex-shrink-0 rounded-2xl border border-bow-yellow/40 bg-bow-yellow/15 px-4 py-3 text-sm text-bow-cream backdrop-blur-md [@media(max-height:740px)]:py-2.5"
      role="status"
      aria-live="polite"
    >
      <div className="font-semibold">
        {bonus ? 'Seu cashback está acabando' : 'Sua recarga vai parar em breve'}
      </div>
      <p className="mt-1 text-bow-cream/80">
        {bonus
          ? 'A recarga para quando ele acabar.'
          : 'O saldo está acabando. Adicione saldo pra continuar carregando, sem encerrar a recarga.'}
      </p>
      {!bonus && (
        <button
          type="button"
          onClick={onTopUp}
          className="mt-2.5 inline-flex h-10 items-center justify-center gap-1 rounded-full bg-bow-cream px-4 text-sm font-bold text-bow-royal transition active:scale-[0.99] [@media(max-height:740px)]:mt-2 [@media(max-height:740px)]:h-9"
        >
          Adicionar saldo
          <ChevronRightIcon />
        </button>
      )}
    </div>
  );
}

/** ETAs até 80%/100% — estimadas no app (`useChargeEta`); só renderiza
 * quando a estimativa estabilizou (design `EtaPair`, tom dark). */
function EtaCards({ eta }: { eta: ChargeEta }) {
  const card = (tag: string, tagColor: string, min: number, at: string) => (
    <div className="flex-1 rounded-[18px] border border-bow-cream/[0.12] bg-bow-cream/[0.08] px-4 py-3 [@media(max-height:740px)]:py-2">
      <span
        className="text-[11px] font-bold tracking-[0.04em]"
        style={{ color: tagColor }}
      >
        {tag}
      </span>
      <div className="mt-1 flex items-baseline gap-1.5 [@media(max-height:740px)]:mt-0.5">
        <span className="text-2xl font-semibold tracking-tight text-bow-cream [@media(max-height:740px)]:text-xl">
          {min}
        </span>
        <span className="text-[13px] text-bow-cream/60">min · {at}</span>
      </div>
    </div>
  );
  return (
    <div className="flex flex-shrink-0 gap-2.5">
      {eta.to80 && card('ATÉ 80%', '#7FE0B0', eta.to80.min, eta.to80.at)}
      {card('ATÉ 100%', '#7FD8EE', eta.to100.min, eta.to100.at)}
    </div>
  );
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

function BoltIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="#FFB92A"
      aria-hidden
    >
      <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}
